/**
 * A case's documents — the checklist, what has arrived, and the deliverables
 * (spec §F, plan §4). Redrawn in Wave 15C (C14 §10.6): both halves are the
 * kit's table, a row's verbs sit in its own menu, sending a checklist item back
 * asks for the reason in a dialog, and sharing is a real dialog.
 *
 * `CaseDocumentsPanel` is the content, and it is two places: the **Documents**
 * tab of the case's record, and the page at `/cases/:caseId/documents` that
 * the client's page links to (the address is unchanged).
 *
 * Three things this screen does that are not obvious:
 *
 * - **It shows the checklist before the files.** What is outstanding is the
 *   question a firm opens this page with; a list of what has already arrived
 *   answers a question nobody asked.
 * - **The guardrail's warning is shown verbatim and the upload is already
 *   done.** The copy never says "we did not attach this" — it did (FR-E14).
 * - **The share control says what happened, not what could happen.** Once
 *   shared it reads *"Shared with client"* and is disabled with the reason,
 *   because a control that still looks pressable is a control someone presses.
 *
 * And from C27 (spec §10.19 item 8; UAT reports #4 and #17):
 *
 * - **A file already on the case can answer a checklist item.** Each document's
 *   menu has *Answers checklist item…*, and the general Upload can name the
 *   item it answers. Both make the item *received* exactly as an upload against
 *   the item does; a file is still the only way in.
 * - **The client is the case's own**, passed in by whoever holds the case. It
 *   used to be read off the first checklist row or document, so a new case with
 *   neither refused every upload with *"This case has no client on file yet."*
 */

import {
  Alert,
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  DropdownMenu,
  type DropdownMenuItem,
  PageHeader,
  Select,
  buttonClass,
} from '@fiveox/ui'
import {
  AlertTriangle,
  ChevronDown,
  ClipboardCheck,
  ClipboardList,
  Download,
  FileText,
  FileUp,
  Package,
  RefreshCw,
} from 'lucide-react'
import { type Ref, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router'

import {
  BackLink,
  DialogActions,
  FORM_GRID,
  FormRow,
  type ListColumn,
  ListTable,
  RecordSection,
  StatusBadge,
  useIsPhone,
  useListTable,
} from '../../components/page'
import { DOCUMENT, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import { cases } from '../../lib/api/cases'
import {
  type DocumentRequestRow,
  type DocumentRow,
  type DocumentVocabulary,
  type UploadResult,
  APPROVAL_LABELS,
  HANDLING_LABELS,
  SOURCE_LABELS,
  approvalState,
  documents,
  fileSize,
  guardrailNotice,
  handlingOptions,
  linkableRequests,
  outstandingCount,
  requestActions,
  shareState,
} from '../../lib/api/documents'
import { ShareDialog } from './ShareDialog'

function Warnings({ result }: { result: UploadResult | null }) {
  const notice = guardrailNotice(result)
  if (notice.tone === 'none') return null
  return (
    <Alert tone="warning">
      <div className="flex gap-2">
        <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
        <div className="space-y-1">
          {/* Verbatim. The message is written for somebody mid-upload with a due
              date, and re-wording it here would be a second copy to keep true. */}
          {notice.warnings.map((warning) => (
            <p key={warning.code} className="text-sm">
              {warning.message}
            </p>
          ))}
          <p className="text-xs">The document has been attached — this is only worth a look.</p>
        </div>
      </div>
    </Alert>
  )
}

/** A file picker dressed as a small outline button — a `<label>` round a
 *  hidden input, so the whole thing is one keyboard-reachable control. */
function UploadButton({
  label,
  disabled,
  onFile,
  inputRef,
}: {
  label: string
  disabled?: boolean
  onFile: (file: File) => void
  inputRef?: Ref<HTMLInputElement>
}) {
  return (
    <label
      className={buttonClass({
        variant: 'outline',
        size: 'sm',
        className: `inline-flex cursor-pointer items-center gap-1.5 ${disabled ? 'pointer-events-none opacity-60' : ''}`,
      })}
    >
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        disabled={disabled}
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) onFile(file)
        }}
      />
      <FileUp size={14} aria-hidden /> {label}
    </label>
  )
}

/**
 * *Answers checklist item…* — C27 (spec §10.19 item 8). The item is required:
 * the placeholder is not a choice (UAT report #13), and pressing *Link* with
 * none chosen says so under the field instead of sending nothing.
 */
function LinkDialog({
  document: row,
  items,
  onClose,
  onLinked,
}: {
  document: DocumentRow
  items: DocumentRequestRow[]
  onClose: () => void
  onLinked: () => void
}) {
  const [chosen, setChosen] = useState('')
  const [missing, setMissing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const picked = items.find((item) => item.id === chosen)

  const link = async () => {
    if (!chosen) {
      setMissing(true)
      return
    }
    setSaving(true)
    setError(null)
    try {
      await documents.update(row.id, { request_id: chosen })
      onLinked()
    } catch (err) {
      // The service's own sentence: "already verified", "a different case".
      setError(err instanceof ApiError ? err.message : 'The file could not be linked.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} title="Answers checklist item" error={error} width="lg">
      <p className="text-muted-foreground mb-4 text-sm break-all">{row.title || row.filename}</p>
      <div className={FORM_GRID}>
        <FormRow label="Checklist item" htmlFor="link-item" required>
          <Select
            id="link-item"
            value={chosen}
            required
            aria-required="true"
            onChange={(e) => {
              setChosen(e.target.value)
              setMissing(false)
            }}
            error={missing ? 'Choose the item this file answers.' : undefined}
            hint={
              picked?.document
                ? `It already has ${picked.document.filename}; that becomes an earlier version.`
                : 'The item is marked received, as if the file had been uploaded against it.'
            }
          >
            <option value="" disabled hidden>
              Choose an item…
            </option>
            {items.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </Select>
        </FormRow>
      </div>
      <DialogActions>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button loading={saving} loadingText="Linking…" onClick={() => void link()}>
          Link
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export function CaseDocumentsPanel({
  caseId,
  clientId: caseClientId = '',
}: {
  caseId: string
  /** The case's own client (UAT report #17). Read from the rows only when a
   *  caller has not passed it — which a new, empty case has none of. */
  clientId?: string
}) {
  const { can } = useEvaluation()
  const canCreate = can(DOCUMENT, 'can_create')
  const canUpdate = can(DOCUMENT, 'can_update')
  const fileRef = useRef<HTMLInputElement>(null)
  const phone = useIsPhone()

  const [vocab, setVocab] = useState<DocumentVocabulary | null>(null)
  const [requests, setRequests] = useState<DocumentRequestRow[]>([])
  const [rows, setRows] = useState<DocumentRow[]>([])
  const [rowClientId, setRowClientId] = useState<string>('')
  const clientId = caseClientId || rowClientId
  /** The item the general Upload answers — optional; empty is the case generally. */
  const [uploadFor, setUploadFor] = useState('')
  const [linking, setLinking] = useState<DocumentRow | null>(null)
  const [lastUpload, setLastUpload] = useState<UploadResult | null>(null)
  const [sharing, setSharing] = useState<DocumentRow | null>(null)
  const [rejecting, setRejecting] = useState<DocumentRequestRow | null>(null)
  const [rejectError, setRejectError] = useState<string | null>(null)
  const [rowError, setRowError] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void documents
      .vocabulary()
      .then((body) => setVocab(body.data))
      .catch(() => setError('Your permissions could not be read.'))
  }, [])

  const load = useCallback(async () => {
    const [checklist, files] = await Promise.all([
      documents.requests({ case_id: caseId }),
      documents.list({ case_id: caseId }),
    ])
    setRequests(checklist.data)
    setRows(files.data)
    // A fallback only: the caller passes the case's own client (UAT report
    // #17), and a new case has no row to read it from.
    setRowClientId(checklist.data[0]?.client_id ?? files.data[0]?.client_id ?? '')
    setLoading(false)
  }, [caseId])

  useEffect(() => {
    setLoading(true)
    void load().catch((err) => {
      setError(err instanceof ApiError ? err.message : 'The documents could not be loaded.')
      setLoading(false)
    })
  }, [load])

  const upload = async (file: File, requestId?: string) => {
    if (!clientId) {
      setError('This case has no client on file yet.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const result = await documents.upload({
        file,
        client_id: clientId,
        case_id: caseId,
        request_id: requestId,
      })
      // Shown, and the document is already attached. Never a refusal.
      setLastUpload(result)
      if (!requestId || requestId === uploadFor) setUploadFor('')
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The document could not be uploaded.')
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  /** A row's own request: its failure is said on the row, in the service's
   *  words (a `CONFLICT` about sharing order is better than anything here). */
  const rowAct = async (id: string, fn: () => Promise<unknown>, fallback: string) => {
    setBusy(true)
    setRowError((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
    try {
      await fn()
      await load()
    } catch (err) {
      setRowError((prev) => ({ ...prev, [id]: err instanceof ApiError ? err.message : fallback }))
    } finally {
      setBusy(false)
    }
  }

  const sync = async () => {
    setBusy(true)
    try {
      await documents.syncRequests(caseId)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The checklist could not be refreshed.')
    } finally {
      setBusy(false)
    }
  }

  const reject = async (reason: string) => {
    if (!rejecting) return
    setRejectError(null)
    try {
      await documents.setRequestState(rejecting.id, 'rejected', reason)
      setRejecting(null)
      await load()
    } catch (err) {
      setRejectError(err instanceof ApiError ? err.message : 'That could not be recorded.')
    }
  }

  const open = async (row: DocumentRow) => {
    const body = await documents.downloadUrl(row.id, 'download')
    // A five-minute capability. Used and dropped — never stored, never on a
    // message.
    window.open(body.data.url, '_blank', 'noopener')
  }

  const zip = async () => {
    const blob = await documents.downloadZip(caseId)
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = 'documents.zip'
    anchor.click()
    URL.revokeObjectURL(url)
  }

  // -- the checklist ---------------------------------------------------------

  const requestColumns = useMemo<ListColumn<DocumentRequestRow>[]>(
    () => [
      {
        key: 'name',
        label: 'What you asked for',
        width: 240,
        required: true,
        phone: true,
        sortField: 'display_order',
        render: (row) => (
          <div className="min-w-0 space-y-0.5">
            <p className="text-foreground font-medium">
              {row.name}
              {!row.is_required && (
                <span className="text-muted-foreground ml-2 text-xs font-normal">optional</span>
              )}
            </p>
            {row.help_text && <p className="text-muted-foreground text-xs">{row.help_text}</p>}
            {row.document && (
              <p className="text-muted-foreground text-xs">
                {row.document.filename} · v{row.document.version}
              </p>
            )}
            {row.state === 'rejected' && row.reason && (
              <p className="text-destructive text-xs">Sent back: {row.reason}</p>
            )}
            {rowError[row.id] && <p className="text-destructive text-xs">{rowError[row.id]}</p>}
            {/* The state column is not shown on a phone; its badge is here. */}
            <span className="sm:hidden">
              <StatusBadge kind="request" status={row.state} />
            </span>
          </div>
        ),
      },
      {
        key: 'status',
        label: 'State',
        width: 150,
        sortField: 'state',
        render: (row) => <StatusBadge kind="request" status={row.state} />,
      },
    ],
    [rowError],
  )
  const checklist = useListTable(requests, {
    columns: requestColumns,
    initialSort: { field: 'display_order', dir: 'asc' },
    pageSize: 50,
  })

  const requestTrailing =
    canCreate || canUpdate
      ? {
          header: <span className="sr-only">Actions</span>,
          // Narrow on a phone, so the name keeps room; the buttons wrap.
          width: phone ? 120 : 290,
          cell: (row: DocumentRequestRow) => (
            <div className="flex flex-wrap justify-end gap-2" onClick={(e) => e.stopPropagation()}>
              {canCreate && (
                <UploadButton
                  label={row.document ? 'Replace' : 'Upload'}
                  disabled={busy}
                  onFile={(file) => void upload(file, row.id)}
                />
              )}
              {canUpdate &&
                requestActions(row).map((to) =>
                  to === 'verified' ? (
                    <Button
                      key={to}
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() =>
                        void rowAct(
                          row.id,
                          () => documents.setRequestState(row.id, 'verified'),
                          'That could not be recorded.',
                        )
                      }
                    >
                      Verify
                    </Button>
                  ) : (
                    <Button
                      key={to}
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => {
                        setRejectError(null)
                        setRejecting(row)
                      }}
                    >
                      Send back
                    </Button>
                  ),
                )}
            </div>
          ),
        }
      : undefined

  // -- the files -------------------------------------------------------------

  const documentColumns = useMemo<ListColumn<DocumentRow>[]>(
    () => [
      {
        key: 'title',
        label: 'Document',
        width: 260,
        required: true,
        phone: true,
        sortField: 'filename',
        render: (row) => (
          <div className="min-w-0 space-y-0.5">
            <Button variant="link" onClick={() => void open(row)}>
              <span className="break-all text-left">{row.title || row.filename}</span>
            </Button>
            <p className="text-muted-foreground text-xs">
              {fileSize(row.size_bytes)} · v{row.version} · {SOURCE_LABELS[row.source]}
              {row.is_current ? '' : ' · superseded'}
            </p>
            {row.guardrail?.warnings?.length ? (
              <p className="text-muted-foreground text-xs">
                <AlertTriangle size={12} className="mr-1 inline" aria-hidden />
                {row.guardrail.warnings.map((w) => w.message).join(' ')}
              </p>
            ) : null}
            {row.approval_status === 'rejected' && row.approval_note && (
              <p className="text-destructive text-xs">The client said: {row.approval_note}</p>
            )}
            {rowError[row.id] && <p className="text-destructive text-xs">{rowError[row.id]}</p>}
          </div>
        ),
      },
      {
        key: 'handling',
        label: 'Handling',
        width: 120,
        sortField: 'handling',
        render: (row) => <Badge size="sm">{HANDLING_LABELS[row.handling]}</Badge>,
      },
      {
        key: 'status',
        label: 'With your client',
        width: 170,
        render: (row) => (
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge size="sm" tone={row.shared_at ? 'info' : 'neutral'}>
              {row.shared_at ? 'Shared' : 'Not shared'}
            </Badge>
            {row.approval_status !== 'not_required' && (
              <Badge
                size="sm"
                tone={
                  row.approval_status === 'approved'
                    ? 'success'
                    : row.approval_status === 'rejected'
                      ? 'error'
                      : 'warning'
                }
              >
                {APPROVAL_LABELS[row.approval_status]}
              </Badge>
            )}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowError],
  )
  const files = useListTable(rows, {
    columns: documentColumns,
    search: (row) => [row.title, row.filename],
    pageSize: 50,
  })

  const documentMenu = (row: DocumentRow): DropdownMenuItem[] => {
    const share = shareState(row)
    const approval = approvalState(row, vocab)
    const items: DropdownMenuItem[] = [
      {
        id: 'open',
        label: 'Open',
        icon: <Download size={14} aria-hidden />,
        onSelect: () => void open(row),
      },
    ]
    if (canUpdate) {
      for (const to of handlingOptions(vocab, row.handling)) {
        items.push({
          id: `handling-${to}`,
          label: `Mark ${HANDLING_LABELS[to].toLowerCase()}`,
          disabled: busy,
          onSelect: () =>
            void rowAct(
              row.id,
              () => documents.update(row.id, { handling: to as never }),
              'That could not be saved.',
            ),
        })
      }
      items.push({
        id: 'share',
        label: share.label,
        description: share.reason ?? undefined,
        disabled: !share.enabled,
        separatorBefore: true,
        onSelect: () => setSharing(row),
      })
      // C27 (§10.19 item 8) — a file already here answers an item. Offered
      // only on a current case file that answers nothing yet, and only when
      // there is an item it may answer (not a verified one).
      if (linkableRequests(requests, row).length > 0) {
        items.push({
          id: 'link',
          label: 'Answers checklist item…',
          icon: <ClipboardCheck size={14} aria-hidden />,
          description: 'Marks the item received with this file.',
          disabled: busy,
          onSelect: () => setLinking(row),
        })
      }
      // w7a — ACCEPTANCE §3.2. Without this no document was ever `pending`,
      // and the portal's Approve button never appeared for any client.
      items.push({
        id: 'approval',
        label: approval.label,
        description: approval.reason ?? undefined,
        disabled: !approval.enabled || busy,
        onSelect: () =>
          void rowAct(
            row.id,
            () => documents.askApproval(row.id),
            'The client could not be asked.',
          ),
      })
    }
    return items
  }

  const outstanding = outstandingCount(requests)
  // What the general Upload may answer: the same items a link may (never a
  // verified one — that reopens by an upload against the item itself).
  const openItems = requests.filter((r) => r.state !== 'verified')

  return (
    <div className="space-y-6">
      {error && <Alert tone="error">{error}</Alert>}
      <Warnings result={lastUpload} />

      {/* The checklist first: what is outstanding is the question a firm opens
          this page with. */}
      <RecordSection
        id="documents-checklist"
        icon={<ClipboardList size={18} aria-hidden />}
        title="What you’ve asked for"
        description={
          loading
            ? 'Loading…'
            : requests.length === 0
              ? 'No checklist on this case.'
              : outstanding === 0
                ? 'Nothing outstanding.'
                : `${outstanding} still to come in.`
        }
      >
        {/* In the body, not the header: beside the heading they squeeze it to a
            word a line on a phone (RecordSection gives its title no minimum). */}
        {canCreate && (
          <div className="mb-4 flex flex-wrap justify-end gap-2">
            <Button size="sm" variant="outline" disabled={busy} onClick={() => void sync()}>
              <RefreshCw size={14} aria-hidden /> Refresh from the service
            </Button>
          </div>
        )}
        <ListTable
          list={checklist}
          rowKey={(row) => row.id}
          loading={loading}
          trailing={requestTrailing}
          empty={
            <span className="text-muted-foreground text-sm">
              This service has no document checklist. Add one on the service, then refresh it here.
            </span>
          }
        />
      </RecordSection>

      <RecordSection
        id="documents-files"
        icon={<FileText size={18} aria-hidden />}
        title="Documents"
        description={loading ? 'Loading…' : `${rows.length} on this case.`}
      >
        {(rows.length > 0 || canCreate) && (
          <div className="mb-4 flex flex-wrap justify-end gap-2">
            {rows.length > 0 && (
              <Button size="sm" variant="ghost" onClick={() => void zip()}>
                <Package size={14} aria-hidden /> Download all
              </Button>
            )}
            {canCreate && openItems.length > 0 && (
              <Select
                id="upload-for"
                aria-label="Answers checklist item"
                value={uploadFor}
                onChange={(e) => setUploadFor(e.target.value)}
                disabled={busy}
                size="sm"
              >
                {/* A real choice, not a placeholder: a file on the case generally. */}
                <option value="">Not for a checklist item</option>
                {openItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    For: {item.name}
                  </option>
                ))}
              </Select>
            )}
            {canCreate && (
              <UploadButton
                label="Upload"
                disabled={busy}
                inputRef={fileRef}
                onFile={(file) => void upload(file, uploadFor || undefined)}
              />
            )}
          </div>
        )}
        <ListTable
          list={files}
          rowKey={(row) => row.id}
          loading={loading}
          trailing={{
            header: <span className="sr-only">Actions</span>,
            width: 110,
            cell: (row) => (
              <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenu
                  label={`Actions for ${row.title || row.filename}`}
                  items={documentMenu(row)}
                  className="w-72"
                  trigger={({ ref, open: _open, ...props }) => (
                    <button
                      ref={ref}
                      type="button"
                      className={buttonClass({ variant: 'outline', size: 'sm' })}
                      {...props}
                    >
                      Actions <ChevronDown size={14} aria-hidden />
                    </button>
                  )}
                />
              </div>
            ),
          }}
          empty={
            <span className="text-muted-foreground text-sm">
              Nothing here yet. Upload a file, or wait for your client to send one from the portal.
            </span>
          }
        />
      </RecordSection>

      <ConfirmDialog
        open={rejecting !== null}
        onClose={() => setRejecting(null)}
        title={rejecting ? `Send back “${rejecting.name}”` : 'Send back'}
        confirmLabel="Send back"
        cancelLabel="Keep it"
        error={rejectError}
        reason={{
          label: 'What was wrong with it?',
          placeholder: 'This is last quarter’s register.',
          required: 'Say what was wrong — your client is shown this and acts on it.',
        }}
        onConfirm={({ reason }) => void reject(reason)}
      >
        <p>Your client is asked for it again, with your reason.</p>
      </ConfirmDialog>

      {linking && (
        <LinkDialog
          document={linking}
          items={linkableRequests(requests, linking)}
          onClose={() => setLinking(null)}
          onLinked={() => {
            setLinking(null)
            void load()
          }}
        />
      )}

      {sharing && (
        <ShareDialog
          document={sharing}
          onClose={() => setSharing(null)}
          onShared={() => {
            setSharing(null)
            void load()
          }}
        />
      )}
    </div>
  )
}

/** `/cases/:caseId/documents` — the panel on a page of its own, for the links
 *  that already point here (the client's page). */
export function CaseDocuments() {
  const { caseId = '' } = useParams()
  // The case's own client (UAT report #17) — the panel cannot read it off an
  // empty case's rows. A failed read leaves the panel's own fallback.
  const [clientId, setClientId] = useState('')
  useEffect(() => {
    if (!caseId) return
    void cases
      .read(caseId)
      .then((body) => setClientId(body.data.case.client_id))
      .catch(() => setClientId(''))
  }, [caseId])
  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        back={<BackLink back={{ to: `/cases/${caseId}`, label: 'The case' }} />}
        title="Documents"
        description="What you have asked your client for, what has arrived, and what you have shared."
      />
      <CaseDocumentsPanel caseId={caseId} clientId={clientId} />
    </div>
  )
}
